import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { getAllLogo } from '../services/Logo_service';
import { getAllDemande } from '../services/demande_service';
import { getUsersWithOtherProfession } from '../services/User_service';

type BadgeKeys = 'logos' | 'demandes';

interface State {
  [key: string]: number;
}

type Action =
  | { type: 'SET_BADGE'; badge: BadgeKeys; value: number }
  | { type: 'INCREMENT_BADGE'; badge: BadgeKeys; value: number }
  | { type: 'RESET_BADGE'; badge: BadgeKeys }
  | { type: 'RESET_ALL' };

const initialState: State = {
  logos: 0,
  demandes: 0, // ✅ Ce badge représente : demandes en attente + inscriptions
};

function badgeReducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_BADGE':
      return { ...state, [action.badge]: action.value };
    case 'INCREMENT_BADGE':
      return { ...state, [action.badge]: (state[action.badge] || 0) + action.value };
    case 'RESET_BADGE':
      return { ...state, [action.badge]: 0 };
    case 'RESET_ALL':
      return initialState;
    default:
      return state;
  }
}

interface BadgeContextType {
  logos: number;
  demandes: number;
  ajouterBadge: (badge: BadgeKeys, value: number) => void;
  mettreAJourBadge: (badge: BadgeKeys, value: number) => void;
  resetBadge: (badge: BadgeKeys) => void;
  rechargerBadges: () => void;
  resetAllBadgesVus: () => void;
}

const BadgeContext = createContext<BadgeContextType>({
  logos: 0,
  demandes: 0,
  ajouterBadge: () => {},
  mettreAJourBadge: () => {},
  resetBadge: (badge: BadgeKeys) => {},
  rechargerBadges: () => {},
  resetAllBadgesVus: () => {},
});

export const useBadge = () => useContext(BadgeContext);

export const BadgeProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(badgeReducer, initialState);

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const logosData = await getAllLogo();
      const pendingLogos = logosData.filter((logo: any) => logo.logo_status === 'pending');
      dispatch({ type: 'SET_BADGE', badge: 'logos', value: pendingLogos.length });

      const demandesData = await getAllDemande();
      const pendingDemandes = demandesData.filter((d: any) => d.status === 0);

      const utilisateursData = await getUsersWithOtherProfession();
      const pendingInscriptionsCount = utilisateursData.length;

      const totalDemandes = pendingDemandes.length + pendingInscriptionsCount;
      dispatch({ type: 'SET_BADGE', badge: 'demandes', value: totalDemandes });

    } catch (error) {
      console.error('Erreur dans BadgeContext:', error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <BadgeContext.Provider
      value={{
        logos: state.logos,
        demandes: state.demandes,
        ajouterBadge: (badge, value) => dispatch({ type: 'INCREMENT_BADGE', badge, value }),
        mettreAJourBadge: (badge, value) => dispatch({ type: 'SET_BADGE', badge, value }),
        resetBadge: (badge) => dispatch({ type: 'RESET_BADGE', badge }),
        rechargerBadges: fetchData,
        resetAllBadgesVus: () => fetchData(),
      }}
    >
      {children}
    </BadgeContext.Provider>
  );
};
